import { lazy, Suspense, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import FullSizeLoading from "components/FullSizeLoading";
import useAcceptServiceEstimate from "hooks/api/useAcceptServiceEstimate";
import useCancelOption from "hooks/api/useCancelOption";
import useDownloadServiceEstimatePdf from "hooks/api/useDownloadServiceEstimatePdf";
import useFindOption from "hooks/api/useFindOption";
import useFindRequestServiceById from "hooks/api/useFindRequestServiceById";
import useRejectServiceEstimate from "hooks/api/useRejectServiceEstimate";
import { useEventListener } from "hooks/effects";
import useErrorModalStatusContext from "hooks/useErrorModalStatusContext";
import { useInfoModal, useWarningModal } from "hooks/useModal";
import usePdfDownloadProvider from "hooks/usePdfDownloadProvider";
import { EstimateWip, OptionDetail, RequestService } from "pages/OptionDetail/constants";
import { isBrowser, isTablet } from "utils/deviceDetect";
import { markOrderSheetViewed, readViewedOrderSheetEstimateIds } from "utils/orderSheetViewSession";

const ServiceDetailPresenter = lazy(() =>
  isBrowser
    ? import("pages/ServiceDetail/pc/ServiceDetailPresenter")
    : isTablet
      ? import("pages/ServiceDetail/sp/ServiceDetailPresenter")
      : import("pages/ServiceDetail/sp/ServiceDetailPresenter")
);

export type ViewState = "list" | "accept" | "reject";

export type ServiceDetailPresenterProps = {
  option: OptionDetail;
  service: RequestService;
  viewState: ViewState;
  selectedEstimate: EstimateWip | null;
  optionDetailModalVisible: boolean;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
  setSelectedEstimate: React.Dispatch<React.SetStateAction<EstimateWip | null>>;
  handleNavigateServiceList: () => void;
  handleCancelOption: () => void;
  handleNavigateOptionEdit: (optionId: string) => void;
  handleRejectEstimate: () => void;
  handleAcceptEstimate: () => void;
  handleClickDownloadPdf: (estimateId: string) => void;
  handleClickDownloadIssuedOrderSheet: (estimateId: string) => void;
  handleOpenDetailModal: (optionId: string) => void;
  handleCloseModal: () => void;
  formValues: {
    rejectedReason: string;
    remarks: string;
    agreed: boolean;
    hasCheckedOrderSheet: boolean;
    setRejectedReason: React.Dispatch<React.SetStateAction<string>>;
    setRemarks: React.Dispatch<React.SetStateAction<string>>;
    setAgreed: React.Dispatch<React.SetStateAction<boolean>>;
    handleClickDownloadOrderSheet: () => Promise<void>;
    errors: {
      isErrorRemarks: boolean;
    };
  };
};

const ServiceDetailContainer = () => {
  const [infoModalState, setInfoModalState] = useState<{ title: string }>({ title: "" });
  const [warningModalState, setWarningModalState] = useState<{ title: string; label: string; onClick: () => void }>({
    title: "",
    label: "",
    onClick: () => null,
  });
  const [viewState, setViewState] = useState<ViewState>("list");
  const [selectedEstimate, setSelectedEstimate] = useState<EstimateWip | null>(null);
  const [rejectedReason, setRejectedReason] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("");
  const [agreed, setAgreed] = useState<boolean>(false);
  // 発注者が注文書の内容を確認したうえで同意する運びにするため、
  // 注文書を開くまで同意チェックを操作できないようにする。
  // 真偽値ではなく確認した見積のIDを持つことで、提案を切り替えたときに確認済みが持ち越されない。
  // SP は注文書を開くとアプリから離脱するため、確認済みは sessionStorage から復元する。
  const [viewedOrderSheetEstimateIds, setViewedOrderSheetEstimateIds] = useState<string[]>(
    readViewedOrderSheetEstimateIds
  );
  const [optionDetailModalVisible, setOptionDetailModalVisible] = useState(false);

  const { id, serviceId } = useParams() as { id: string; serviceId: string };
  const { option, mutate } = useFindOption(id);
  const { downloadServiceEstimatePdfByEstimateId, downloadOrderSheetPdfByEstimateId } = usePdfDownloadProvider();
  const navigate = useNavigate();
  const { cancelOption } = useCancelOption();
  const { showInfoModal, InfoModal } = useInfoModal();
  const { showWarningModal, hideWarningModal, WarningModal } = useWarningModal();
  const { showErrorModal } = useErrorModalStatusContext();
  const { requestService, mutate: mutateRequestService } = useFindRequestServiceById(id, serviceId);
  const { acceptEstimate } = useAcceptServiceEstimate();
  const { rejectEstimate } = useRejectServiceEstimate();

  // ブラウザの戻る/進むでページが復元されると、React の state ごと発注直前の状態が戻り、
  // 同意したまま発注を待つ画面が復活してしまう。復元時は同意を取り消す。
  // 一方で注文書を開いた事実は取り消さない。SP では注文書を開くこと自体が離脱と復帰になるため、
  // ここで取り消すと発注できなくなる。復帰後にもう一度同意してもらうことで趣旨は保てる。
  useEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    setAgreed(false);
    setViewedOrderSheetEstimateIds(readViewedOrderSheetEstimateIds());
  });

  const isErrorRemarks = rejectedReason.length === 0;

  const hasCheckedOrderSheet =
    Boolean(selectedEstimate) && viewedOrderSheetEstimateIds.includes(selectedEstimate?.id ?? "");

  // 確認用注文書は見積書と同じくその場でファイルを保存する。
  // ダウンロードできたときだけ確認済みとし、同意チェックを開ける。
  const handleClickDownloadOrderSheet = async () => {
    if (!selectedEstimate) return;
    const downloaded = await downloadOrderSheetPdfByEstimateId(
      id,
      serviceId,
      selectedEstimate.id,
      "draft",
      requestService?.name ?? ""
    );
    if (downloaded) {
      markOrderSheetViewed(selectedEstimate.id);
      setViewedOrderSheetEstimateIds(readViewedOrderSheetEstimateIds());
    }
  };

  const handleClickDownloadIssuedOrderSheet = (estimateId: string) => {
    downloadOrderSheetPdfByEstimateId(id, serviceId, estimateId, "issued", requestService?.name ?? "");
  };

  const formValues = {
    rejectedReason,
    remarks,
    agreed,
    hasCheckedOrderSheet,
    setRejectedReason,
    setRemarks,
    setAgreed,
    handleClickDownloadOrderSheet,
    errors: {
      isErrorRemarks,
    },
  };

  const cancelOptionHandler = async () => {
    const status = await cancelOption(id).catch(() => {
      showErrorModal({ httpMethod: "put" });
      hideWarningModal();
    });
    if (status === 200) {
      hideWarningModal();
      openCompleteModal();
      await mutate();
    }
  };

  const openSubmitModal = () => {
    setWarningModalState({
      title: "依頼を停止しますか？",
      label: "停止する",
      onClick: cancelOptionHandler,
    });
    showWarningModal();
  };

  const openCompleteModal = () => {
    setInfoModalState({
      title: "依頼を停止しました",
    });
    showInfoModal();
  };
  const openAcceptedModal = async () => {
    setInfoModalState({
      title: "発注しました",
    });
    showInfoModal();
    setViewState("list");
    setSelectedEstimate(null);
    setRemarks("");
    setAgreed(false);
    await mutateRequestService();
  };
  const openRejectedModal = async () => {
    setInfoModalState({
      title: "見送りました",
    });
    showInfoModal();
    setViewState("list");
    setSelectedEstimate(null);
    setRejectedReason("");
    await mutateRequestService();
  };
  const handleCancelOption = () => {
    openSubmitModal();
  };
  const handleNavigateServiceList = () => {
    navigate(`/option/${id}/`);
  };
  const handleNavigateOptionEdit = () => {
    navigate(`/option/${id}/update`);
  };
  const handleRejectEstimate = async () => {
    await rejectEstimate(id, serviceId, selectedEstimate?.id || "", rejectedReason);
    openRejectedModal();
  };
  const handleAcceptEstimate = async () => {
    await acceptEstimate(id, serviceId, selectedEstimate?.id || "", remarks, agreed);
    openAcceptedModal();
  };
  const handleClickDownloadPdf = (estimateId: string) => {
    downloadServiceEstimatePdfByEstimateId(id, serviceId, estimateId);
  };
  const handleOpenDetailModal = () => {
    setOptionDetailModalVisible(true);
  };
  const handleCloseModal = () => {
    setOptionDetailModalVisible(false);
  };

  return (
    <Suspense fallback={<FullSizeLoading />}>
      <ServiceDetailPresenter
        option={option}
        service={requestService}
        viewState={viewState}
        setViewState={setViewState}
        selectedEstimate={selectedEstimate}
        setSelectedEstimate={setSelectedEstimate}
        handleCancelOption={handleCancelOption}
        handleNavigateOptionEdit={handleNavigateOptionEdit}
        handleNavigateServiceList={handleNavigateServiceList}
        handleRejectEstimate={handleRejectEstimate}
        handleAcceptEstimate={handleAcceptEstimate}
        handleClickDownloadPdf={handleClickDownloadPdf}
        handleClickDownloadIssuedOrderSheet={handleClickDownloadIssuedOrderSheet}
        formValues={formValues}
        handleOpenDetailModal={handleOpenDetailModal}
        optionDetailModalVisible={optionDetailModalVisible}
        handleCloseModal={handleCloseModal}
      />
      <WarningModal
        title={warningModalState.title}
        label={warningModalState.label}
        onClick={warningModalState.onClick}
      />
      <InfoModal title={infoModalState.title} />
    </Suspense>
  );
};

export default ServiceDetailContainer;
